import { useContext, useMemo, type ComponentProps } from "react";
import type { RowSelectionState } from "@tanstack/react-table";
import type { Invoice } from "@common/types/invoice";
import { PaginatedInvoicesTableContext } from "@components/invoices/PaginatedInvoicesTable.context";
import { getSelectedInvoices } from "@components/invoices/views/invoicesView/getSelectedInvoices";
import { InvoicesViewContainer } from "@components/invoices/views/invoicesView/InvoicesViewContainer";
import { identity } from "../../chatDrag/identity";
import { useRowChatDrag } from "../../chatDrag/useRowChatDrag";
import { useAddInvoicesToChat } from "../../useAddReferencesToChat";

type InvoicesTaskViewContainerProps = ComponentProps<
    typeof InvoicesViewContainer
> & {
    rowSelection: RowSelectionState;
    onClearSelection: () => void;
};

/**
 * The task's invoices table with rows draggable into the task chat. Renders
 * inside `PaginatedInvoicesTableContextProvider`, which holds the loaded rows.
 */
export const InvoicesTaskViewContainer = (
    props: InvoicesTaskViewContainerProps,
) => {
    const { rowSelection, onClearSelection } = props;
    const { invoices } = useContext(PaginatedInvoicesTableContext);
    const selectedInvoices = useMemo(
        () => getSelectedInvoices(invoices, rowSelection),
        [invoices, rowSelection],
    );
    const { canAddToChat, addInvoicesToChat } = useAddInvoicesToChat();
    const rowDragOut = useRowChatDrag({
        noun: "invoice",
        canAddToChat,
        getRowItem: identity<Invoice>,
        selectedItems: selectedInvoices,
        addToChat: addInvoicesToChat,
        clearSelection: onClearSelection,
    });

    return <InvoicesViewContainer {...props} rowDragOut={rowDragOut} />;
};
